import { useState, type FormEvent } from 'react';
import { useAgentPermission } from '@entities/user';
import { useListKnowledgeQuery, useUploadKnowledgeMutation } from '@shared/api';
import { ACCEPT_KB_DOC } from '@shared/lib/fileAccept';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Input, Label, Select } from '@shared/ui/Input';
import { Modal } from '@shared/ui/Modal';
import {
  DOC_TYPES,
  fmtSize,
  TARGET_ENTITY_OPTIONS,
  TEMPLATE_KIND_BY_DOC_TYPE,
} from './constants';
import {
  ErrorBox,
  FileList,
  FileName,
  FileRow,
  FileSize,
  Remove,
  UploadFieldCompact,
  UploadFieldSpaced,
  UploadGridTwoCol,
  UploadModalActions,
  WarnNote,
} from './styles';
import type { TargetEntityOption, UploadModalProps } from './types';

export function UploadModal({ open, onClose, defaultDocType }: UploadModalProps) {
  const { can_write } = useAgentPermission('statut');
  const [upload, { isLoading }] = useUploadKnowledgeMutation();
  // Полный список — чтобы понять, занят ли уже выбранный template-тип
  // включённым шаблоном (тот же кэш-ключ, что и базовый список страницы).
  const { data: allDocs } = useListKnowledgeQuery();
  const [files, setFiles] = useState<File[]>([]);
  const [docType, setDocType] = useState(defaultDocType ?? 'gost');
  const [version, setVersion] = useState('');
  const [description, setDescription] = useState('');
  const [targetEntity, setTargetEntity] = useState<TargetEntityOption>('any');
  const [error, setError] = useState<string | null>(null);

  const isTemplate = docType in TEMPLATE_KIND_BY_DOC_TYPE;

  // Уже включённый шаблон того же типа: при загрузке нового он станет
  // активным вместо текущего — предупреждаем юриста заранее.
  const occupiedTemplate =
    isTemplate ? allDocs?.items.find((d) => d.document_type === docType && d.is_enabled) : undefined;

  function reset() {
    setFiles([]);
    setDocType(defaultDocType ?? 'gost');
    setVersion('');
    setDescription('');
    setTargetEntity('any');
    setError(null);
  }

  function addFiles(newOnes: File[]) {
    setFiles((prev) => {
      const map = new Map(prev.map((f) => [`${f.name}:${f.size}`, f]));
      for (const f of newOnes) map.set(`${f.name}:${f.size}`, f);
      return Array.from(map.values());
    });
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (files.length === 0) {
      setError('Выберите хотя бы один файл');
      return;
    }
    const form = new FormData();
    for (const f of files) form.append('file', f);
    form.append('document_type', docType);
    if (version) form.append('version', version);
    if (description) form.append('description', description);
    if (isTemplate) {
      form.append('template_kind', TEMPLATE_KIND_BY_DOC_TYPE[docType]);
      form.append('target_entity', targetEntity);
    }
    try {
      const res = await upload(form).unwrap();
      if (res.errors.length > 0) {
        setError(`Часть файлов не загружена: ${res.errors.join('; ')}`);
        return;
      }
      reset();
      onClose();
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        'Ошибка загрузки';
      setError(msg);
    }
  }

  return (
    <Modal open={open} title="Загрузить документы в базу знаний" onClose={onClose}>
      <form onSubmit={submit}>
        <FileDropZone
          multiple
          accept={ACCEPT_KB_DOC}
          onFiles={addFiles}
          hint={
            files.length > 0
              ? `Выбрано: ${files.length}. Можно добавить ещё.`
              : 'PDF / DOCX / DOC / TXT / MD. .doc автоматически конвертируется в .docx при индексации.'
          }
        />

        {files.length > 0 && (
          <FileList>
            {files.map((f, i) => (
              <FileRow key={`${f.name}:${f.size}:${i}`}>
                <FileName title={f.name}>{f.name}</FileName>
                <FileSize>{fmtSize(f.size)}</FileSize>
                <Remove
                  type="button"
                  onClick={() => removeFile(i)}
                  aria-label="Убрать файл"
                  title="Убрать файл"
                >
                  ×
                </Remove>
              </FileRow>
            ))}
          </FileList>
        )}

        <UploadGridTwoCol>
          <UploadFieldCompact>
            <Label>Тип</Label>
            <Select value={docType} onChange={(e) => setDocType(e.target.value)}>
              {DOC_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </UploadFieldCompact>
          <UploadFieldCompact>
            <Label>Версия (общая)</Label>
            <Input value={version} onChange={(e) => setVersion(e.target.value)} />
          </UploadFieldCompact>
        </UploadGridTwoCol>
        <UploadFieldSpaced>
          <Label>Описание (общее)</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </UploadFieldSpaced>

        {isTemplate && (
          <UploadFieldSpaced>
            <Label>Адресат шаблона</Label>
            <Select value={targetEntity} onChange={(e) => setTargetEntity(e.target.value as TargetEntityOption)}>
              {TARGET_ENTITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </UploadFieldSpaced>
        )}

        {occupiedTemplate && (
          <WarnNote>
            Для этого типа уже есть активный шаблон «{occupiedTemplate.title}». После загрузки
            новый шаблон станет активным для генерации, а текущий — деактивируется.
          </WarnNote>
        )}

        {error && <ErrorBox>{error}</ErrorBox>}

        <UploadModalActions>
          <Button type="button" $variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            type="submit"
            disabled={isLoading || files.length === 0 || !can_write}
            title={!can_write ? 'Только чтение' : undefined}
          >
            {isLoading ? `Загрузка ${files.length}…` : `Загрузить ${files.length || ''}`.trim()}
          </Button>
        </UploadModalActions>
      </form>
    </Modal>
  );
}
